import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Input,
  Notice,
  PageTitle,
  Select,
  tableClass,
  tdClass,
  thClass,
} from "../components/ui";
import { FilePicker } from "../components/file-picker";
import { ApiError, api } from "../lib/api";
import { formatDateTime, voucherNumber } from "../lib/format";

interface Report {
  fileName: string;
  error?: string;
  parser: { id: string; label: string; version: number; provisional: boolean };
  sheetName: string | null;
  fileWarnings: string[];
  rowsRead: number;
  accepted: number;
  rejected: number;
  duplicates: number;
  withWarnings: number;
  counterpartsToCreate: { docType: number; docNumber: string; name: string }[];
  nameMismatches: { docNumber: string; inFile: string; stored: string }[];
  byPeriod: Record<string, number>;
}
interface Batch {
  id: string;
  status: "staged" | "committed" | "discarded" | "failed";
  direction: "compra" | "venta";
  report: Report;
  createdAt: string;
  committedAt: string | null;
}
interface Row {
  id: string;
  rowNumber: number;
  status: "accepted" | "rejected" | "duplicate";
  reasons: string[];
  warnings: string[];
  raw: Record<string, unknown>;
  normalized: {
    voucher: {
      typeCode: number;
      issueDate: string;
      pointOfSale: number;
      number: number;
      counterpart: { name: string; docNumber: string };
      total: string;
      lines: { kind: string; rate?: string }[];
    };
  } | null;
}

const STATUS = {
  staged: { label: "Esperando confirmación", tone: "warning" },
  committed: { label: "Confirmada", tone: "success" },
  discarded: { label: "Descartada", tone: "neutral" },
  failed: { label: "No se pudo leer", tone: "danger" },
} as const;

export function ImportPage({ companyId }: { companyId: string }) {
  const client = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [parserId, setParserId] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const formats = useQuery({
    queryKey: ["import-formats"],
    queryFn: () =>
      api.get<{ id: string; label: string; provisional: boolean }[]>("/api/imports/formats"),
  });
  const batches = useQuery({
    queryKey: ["imports", companyId],
    queryFn: () => api.get<Batch[]>(`/api/companies/${companyId}/imports`),
  });
  const upload = useMutation({
    mutationFn: () => {
      const form = new FormData();
      if (file) form.append("file", file);
      if (parserId) form.append("parserId", parserId);
      if (sheetName.trim()) form.append("sheetName", sheetName.trim());
      return api.upload<{ batch: Batch }>(`/api/companies/${companyId}/imports`, form);
    },
    onSuccess: async (result) => {
      setSelected(result.batch.id);
      await client.invalidateQueries({ queryKey: ["imports", companyId] });
    },
    onError: (error) => {
      const batchId =
        error instanceof ApiError
          ? (error.details as { batchId?: string } | undefined)?.batchId
          : undefined;
      if (batchId) setSelected(batchId);
    },
  });

  return (
    <>
      <PageTitle subtitle="Subí el archivo tal como lo descargaste: sin renombrar hojas, borrar columnas ni convertir formatos.">
        Importar comprobantes
      </PageTitle>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="Nuevo archivo" className="lg:col-span-1">
          <div className="flex flex-col gap-3 text-sm">
            <FilePicker
              file={file}
              onChange={setFile}
              accept={ACCEPT}
              label="Archivo"
              hint="o arrastralo acá · Excel (.xlsx) o CSV"
            />
            <Select
              value={parserId}
              onChange={(e) => setParserId(e.target.value)}
              aria-label="Formato"
            >
              <option value="">Detectar formato automáticamente</option>
              {formats.data?.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                  {f.provisional ? " (sin validar)" : ""}
                </option>
              ))}
            </Select>
            <Input
              value={sheetName}
              onChange={(e) => setSheetName(e.target.value)}
              placeholder="Hoja (solo si el Excel tiene varias)"
              aria-label="Hoja"
            />
            <ErrorBox error={upload.error} />
            <Button disabled={!file || upload.isPending} onClick={() => upload.mutate()}>
              {upload.isPending ? "Analizando…" : "Analizar archivo"}
            </Button>
            <p className="text-xs text-brand-muted">
              Nada se carga todavía: primero ves el informe completo y después confirmás.{" "}
              <a className="text-brand-primary underline" href="/api/imports/template.csv">
                Descargar la planilla de comprobantes
              </a>{" "}
              para cargas desde otras fuentes.
            </p>
          </div>
        </Card>
        <Card title="Importaciones de esta empresa" className="lg:col-span-2">
          {batches.data?.length ? (
            <table className={tableClass}>
              <thead>
                <tr>
                  <th className={thClass}>Archivo</th>
                  <th className={thClass}>Formato</th>
                  <th className={thClass}>Filas</th>
                  <th className={thClass}>Estado</th>
                  <th className={thClass} />
                </tr>
              </thead>
              <tbody>
                {batches.data.map((b) => (
                  <tr key={b.id} className={selected === b.id ? "bg-brand-tint" : ""}>
                    <td className={tdClass}>
                      <div className="font-medium">{b.report.fileName}</div>
                      <div className="text-xs text-brand-muted">{formatDateTime(b.createdAt)}</div>
                    </td>
                    <td className={tdClass}>{b.report.parser.label}</td>
                    <td className={`${tdClass} tabular text-xs`}>
                      {b.report.accepted} ok · {b.report.rejected} rechazadas ·{" "}
                      {b.report.duplicates} duplicadas
                    </td>
                    <td className={tdClass}>
                      <Badge tone={STATUS[b.status].tone}>{STATUS[b.status].label}</Badge>
                    </td>
                    <td className={`${tdClass} text-right`}>
                      <Button variant="ghost" onClick={() => setSelected(b.id)}>
                        Ver informe
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-brand-muted">Todavía no se importó ningún archivo.</p>
          )}
        </Card>
      </div>
      {selected ? <BatchReport key={selected} batchId={selected} companyId={companyId} /> : null}
    </>
  );
}

function BatchReport({ batchId, companyId }: { batchId: string; companyId: string }) {
  const client = useQueryClient();
  const [filter, setFilter] = useState<"all" | Row["status"]>("all");
  const detail = useQuery({
    queryKey: ["import", batchId],
    queryFn: () => api.get<{ batch: Batch; rows: Row[] }>(`/api/imports/${batchId}`),
  });
  const refresh = async () => {
    await client.invalidateQueries({ queryKey: ["import", batchId] });
    await client.invalidateQueries({ queryKey: ["imports", companyId] });
    await client.invalidateQueries({ queryKey: ["vouchers", companyId] });
    await client.invalidateQueries({ queryKey: ["periods", companyId] });
    await client.invalidateQueries({ queryKey: ["caja", companyId] });
  };
  const commit = useMutation({
    mutationFn: () => api.post<{ created: number }>(`/api/imports/${batchId}/commit`),
    onSuccess: refresh,
  });
  const discard = useMutation({
    mutationFn: () => api.post(`/api/imports/${batchId}/discard`),
    onSuccess: refresh,
  });
  if (!detail.data) return <ErrorBox error={detail.error} />;
  const { batch, rows } = detail.data;
  const r = batch.report;
  const shown = rows.filter((row) => filter === "all" || row.status === filter);
  const firstPeriod = Object.keys(r.byPeriod).sort()[0];

  return (
    <Card
      className="mt-4"
      title={`Informe: ${r.fileName}`}
      actions={
        <a
          className="text-sm text-brand-primary hover:underline"
          href={`/api/imports/${batchId}/file`}
        >
          Descargar original
        </a>
      }
    >
      <div className="flex flex-col gap-3">
        {batch.status === "failed" ? (
          <Notice tone="warning">
            No se pudo leer el archivo: {r.error}. El original quedó guardado y el intento figura en
            la auditoría.
          </Notice>
        ) : null}
        {r.parser.provisional ? (
          <Notice tone="warning">
            Formato «{r.parser.label}» todavía probado con pocos archivos reales: revisá el informe
            con atención antes de confirmar.
          </Notice>
        ) : null}
        {r.fileWarnings
          .filter((w) => !r.parser.provisional || !/sin validar|probado con/i.test(w))
          .map((w) => (
            <Notice key={w}>{w}</Notice>
          ))}
        <div className="grid grid-cols-2 gap-2 text-center md:grid-cols-5">
          {[
            ["Filas leídas", r.rowsRead],
            ["Se cargan", r.accepted],
            ["Rechazadas", r.rejected],
            ["Duplicadas", r.duplicates],
            ["Contrapartes nuevas", r.counterpartsToCreate.length],
          ].map(([label, value]) => (
            <div key={String(label)} className="rounded-md border border-brand-line p-2">
              <div className="text-xl font-semibold tabular">{value}</div>
              <div className="text-xs text-brand-muted">{label}</div>
            </div>
          ))}
        </div>
        <p className="text-sm text-brand-muted">
          Formato: {r.parser.label} v{r.parser.version}
          {r.sheetName ? ` · hoja «${r.sheetName}»` : ""} · Períodos:{" "}
          {Object.entries(r.byPeriod)
            .map(([p, n]) => `${p.slice(5)}/${p.slice(0, 4)} (${n})`)
            .join(", ") || "—"}
        </p>
        {r.nameMismatches.length ? (
          <Notice tone="warning">
            {r.nameMismatches.length} contraparte(s) figuran con otro nombre en el archivo; se
            mantiene el nombre ya cargado. Ej.: «{r.nameMismatches[0]?.inFile}» vs «
            {r.nameMismatches[0]?.stored}».
          </Notice>
        ) : null}
        <ErrorBox error={commit.error ?? discard.error} />
        {commit.data ? (
          <Notice tone="success">
            Se cargaron {commit.data.created} comprobantes.{" "}
            {firstPeriod ? (
              <Link
                to="/empresas/$companyId/comprobantes"
                params={{ companyId }}
                search={{ periodo: firstPeriod, dir: batch.direction }}
                className="underline"
              >
                Verlos
              </Link>
            ) : null}
          </Notice>
        ) : null}
        {batch.status === "staged" ? (
          <div className="flex flex-wrap gap-2">
            <Button disabled={r.accepted === 0 || commit.isPending} onClick={() => commit.mutate()}>
              Confirmar y cargar {r.accepted} comprobante{r.accepted === 1 ? "" : "s"}
            </Button>
            <Button
              variant="secondary"
              disabled={discard.isPending}
              onClick={() => discard.mutate()}
            >
              Descartar
            </Button>
            <span className="self-center text-xs text-brand-muted">
              Las rechazadas y duplicadas no se cargan. El informe queda guardado igual.
            </span>
          </div>
        ) : null}
        <div className="flex gap-1">
          {(["all", "accepted", "rejected", "duplicate"] as const).map((f) => (
            <Button
              key={f}
              variant={filter === f ? "primary" : "secondary"}
              onClick={() => setFilter(f)}
            >
              {
                {
                  all: "Todas",
                  accepted: "Se cargan",
                  rejected: "Rechazadas",
                  duplicate: "Duplicadas",
                }[f]
              }
            </Button>
          ))}
        </div>
        <div className="max-h-[480px] overflow-auto">
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={thClass}>Fila</th>
                <th className={thClass}>Estado</th>
                <th className={thClass}>Comprobante</th>
                <th className={thClass}>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => {
                const v = row.normalized?.voucher;
                return (
                  <tr key={row.id}>
                    <td className={`${tdClass} tabular`}>{row.rowNumber}</td>
                    <td className={tdClass}>
                      <Badge
                        tone={
                          row.status === "accepted"
                            ? "success"
                            : row.status === "duplicate"
                              ? "warning"
                              : "danger"
                        }
                      >
                        {
                          { accepted: "Se carga", rejected: "Rechazada", duplicate: "Duplicada" }[
                            row.status
                          ]
                        }
                      </Badge>
                    </td>
                    <td className={tdClass}>
                      {v ? (
                        <>
                          <div>
                            {v.issueDate.split("-").reverse().join("/")} · tipo {v.typeCode} ·{" "}
                            {voucherNumber(v.pointOfSale, v.number)}
                          </div>
                          <div className="text-xs text-brand-muted">
                            {v.counterpart.name} · total {v.total}
                            {v.lines.filter((l) => l.kind === "gravado").length > 1
                              ? " · alícuotas múltiples"
                              : ""}
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-brand-muted">
                          {Object.values(row.raw).slice(0, 4).join(" · ")}
                        </span>
                      )}
                    </td>
                    <td className={`${tdClass} text-xs`}>
                      {row.reasons.map((reason) => (
                        <div key={reason} className="text-red-800">
                          {reason}
                        </div>
                      ))}
                      {row.warnings.map((w) => (
                        <div key={w} className="text-amber-800">
                          {w}
                        </div>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}

const ACCEPT =
  ".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
